import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, RestaurantMenu } from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { addMenuWeek, updateMenuWeek } from "apis";
import { ALLERGENS, IMenuWeek, MealType, Season } from "types";

const MEAL_TYPES: MealType[] = ["breakfast", "snack", "lunch", "tea_time"];
const SEASONS: Season[] = ["spring", "summer", "autumn", "winter"];
const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

interface Props {
  open: boolean;
  editItem: IMenuWeek | null;
  workingDays: number[];
  onClose: (changed: boolean) => void;
}

type DayMeta = { vegetarian: boolean; allergens: string[] };

const cellKey = (day: number, type: MealType) => `${day}:${type}`;

const MenuWeekDialog: React.FC<Props> = ({
  open,
  editItem,
  workingDays,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(editItem?.year ?? currentYear);
  const [season, setSeason] = useState<Season>(
    (editItem?.season as Season) ?? "spring",
  );
  const [label, setLabel] = useState<string>(editItem?.label ?? "");
  const [cells, setCells] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    editItem?.meals.forEach((m) => {
      initial[cellKey(m.dayOfWeek, m.mealType)] = m.description;
    });
    return initial;
  });
  const [dietaryCells, setDietaryCells] = useState<Record<string, string>>(
    () => {
      const initial: Record<string, string> = {};
      editItem?.meals.forEach((m) => {
        if (m.dietaryDescription)
          initial[cellKey(m.dayOfWeek, m.mealType)] = m.dietaryDescription;
      });
      return initial;
    },
  );
  const [dayMeta, setDayMeta] = useState<Record<number, DayMeta>>(() => {
    const initial: Record<number, DayMeta> = {};
    editItem?.days?.forEach((d) => {
      initial[d.dayOfWeek] = {
        vegetarian: d.vegetarian,
        allergens: d.allergens ?? [],
      };
    });
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  const metaFor = (day: number): DayMeta =>
    dayMeta[day] ?? { vegetarian: false, allergens: [] };

  const setMeta = (day: number, patch: Partial<DayMeta>) =>
    setDayMeta((prev) => ({
      ...prev,
      [day]: { ...metaFor(day), ...patch },
    }));

  const handleSubmit = () => {
    if (!label.trim()) {
      enqueueSnackbar(t("menu.label_required"), { variant: "warning" });
      return;
    }
    const meals = [];
    for (const day of workingDays) {
      for (const type of MEAL_TYPES) {
        const description = (cells[cellKey(day, type)] || "").trim();
        const dietaryDescription = (
          dietaryCells[cellKey(day, type)] || ""
        ).trim();
        if (description || dietaryDescription)
          meals.push({
            dayOfWeek: day,
            mealType: type,
            description,
            dietaryDescription: dietaryDescription || undefined,
          });
      }
    }
    const days = workingDays
      .map((day) => ({
        dayOfWeek: day,
        vegetarian: metaFor(day).vegetarian,
        allergens: metaFor(day).allergens,
      }))
      .filter((d) => d.vegetarian || d.allergens.length > 0);

    const payload = { year, season, label: label.trim(), meals, days };
    setSubmitting(true);
    const request = editItem
      ? updateMenuWeek({ ...payload, id: editItem.id })
      : addMenuWeek(payload);
    request
      .then(() => {
        enqueueSnackbar(editItem ? t("menu.updated") : t("menu.added"), {
          variant: "success",
        });
        onClose(true);
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <RestaurantMenu />
          <Typography variant="h6" fontWeight={700}>
            {editItem ? t("menu.edit") : t("menu.add")}
          </Typography>
        </Box>
        <IconButton
          onClick={() => onClose(false)}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3 }}>
        <Grid container spacing={2} mb={1}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("labels.year")}</InputLabel>
              <Select
                label={t("labels.year")}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("menu.season")}</InputLabel>
              <Select
                label={t("menu.season")}
                value={season}
                onChange={(e) => setSeason(e.target.value as Season)}
              >
                {SEASONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`menu.seasons.${s}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("menu.week_label")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        <TableContainer
          sx={{ mt: 1, borderRadius: 2, border: 1, borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t("menu.meal")}</TableCell>
                {workingDays.map((day) => (
                  <TableCell key={day} sx={{ fontWeight: 700 }}>
                    {t(`menu.weekdays.${DAY_KEYS[day]}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {MEAL_TYPES.map((type) => (
                <TableRow key={type}>
                  <TableCell sx={{ fontWeight: 600 }}>{t(type)}</TableCell>
                  {workingDays.map((day) => (
                    <TableCell key={day} sx={{ verticalAlign: "top" }}>
                      <Stack spacing={1} sx={{ minWidth: 140 }}>
                        <TextField
                          value={cells[cellKey(day, type)] || ""}
                          onChange={(e) =>
                            setCells((prev) => ({
                              ...prev,
                              [cellKey(day, type)]: e.target.value,
                            }))
                          }
                          label={t("menu.option_standard")}
                          fullWidth
                          size="small"
                          multiline
                          variant="standard"
                        />
                        <TextField
                          value={dietaryCells[cellKey(day, type)] || ""}
                          onChange={(e) =>
                            setDietaryCells((prev) => ({
                              ...prev,
                              [cellKey(day, type)]: e.target.value,
                            }))
                          }
                          label={t("menu.option_dietary")}
                          fullWidth
                          size="small"
                          multiline
                          variant="standard"
                        />
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Per-day dietary info */}
        <Divider sx={{ my: 2.5 }} />
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          {t("menu.dietary")}
        </Typography>
        <Stack spacing={1}>
          {workingDays.map((day) => {
            const meta = metaFor(day);
            return (
              <Box
                key={day}
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: 90 }}
                >
                  {t(`menu.weekdays.${DAY_KEYS[day]}`)}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={meta.vegetarian}
                      onChange={(e) =>
                        setMeta(day, { vegetarian: e.target.checked })
                      }
                    />
                  }
                  label={t("menu.vegetarian")}
                />
                <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
                  <InputLabel>{t("menu.allergens")}</InputLabel>
                  <Select
                    multiple
                    value={meta.allergens}
                    onChange={(e) =>
                      setMeta(day, {
                        allergens:
                          typeof e.target.value === "string"
                            ? e.target.value.split(",")
                            : (e.target.value as string[]),
                      })
                    }
                    input={<OutlinedInput label={t("menu.allergens")} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(selected as string[]).map((v) => (
                          <Chip
                            key={v}
                            size="small"
                            label={t(`menu.allergen_list.${v}`)}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {ALLERGENS.map((a) => (
                      <MenuItem key={a} value={a}>
                        {t(`menu.allergen_list.${a}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={submitting}
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : editItem ? (
            t("buttons.save")
          ) : (
            t("buttons.add")
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default MenuWeekDialog;
